import APIClient from "web_modules/APIClient";
import {
  AdditionalIncomePayload,
  AdditionalIncomeShape,
  DocTypes,
  DocShape,
  IncomeAggregationPayload,
} from "@eave/shared/types/api";
import { AdditionalIncomeForm } from "./income_tables/OtherIncomeTable";
import { findFieldValue } from "underwriter_dashboard/shared";

/**
 * Creates an AdditionalIncome record
 * @param  {AdditionalIncomePayload} values A subset of the form values from OtherIncomeModalForm
 */
export const createAdditionalIncome = (values: AdditionalIncomePayload) => {
  return APIClient.post("/additional_income", values);
};

/**
 * Links a document to an income_source
 * @param  {DocShape} document The document object to be updated
 * @param  {number} incomeOwnerEntityID the ID of the income_source that we want to associate with the document
 */
const attachIncomeToDocument = (document: DocShape, incomeOwnerEntityID: number) => {
  const payload = {
    ...document,
    owner_entity_id: incomeOwnerEntityID,
    owner_entity_type: "additional_income",
  };
  return APIClient.put(`/documents/${document.id}`, payload);
};

/**
 * Unlinks a document to an income_source
 * @param  {DocShape} document The document object to be updated
 */
const detachDocumentFromIncomes = (document: DocShape) => {
  const payload = {
    ...document,
    owner_entity_id: null,
    owner_entity_type: null,
  };
  return APIClient.put(`/documents/${document.id}`, payload);
};

/**
 * Uploads a series of documents (from DropZone) as attachments to an income_source
 * @param  {any} files An array of document files (as returned by Dropzone's `onDrop` event handler)
 * @param  {number} owner_entity_id the ID of the additional_income entity associated with this document
 * @param  {number} loan_application_id The loan_application ID
 */
export const addAttachments = async (
  files: any[],
  owner_entity_id: number,
  loan_application_id: number,
) => {
  const additionalData = {
    owner_entity_type: "additional_income",
    owner_entity_id,
    application_id: loan_application_id,
  };

  const promises = files.map(file => {
    return APIClient.dropzoneUpload(
      "/loan_application/attachments",
      DocTypes.IncomeOther,
      file,
      additionalData,
    );
  });

  return Promise.all(promises);
};

/**
 * Updates an existing AdditionalIncome record
 * @param  {AdditionalIncome} values The updated form values from OtherIncomeModalForm
 */
export const updateAdditionalIncome = async (
  updatedValues: AdditionalIncomePayload,
  docsToAttach: DocShape[],
  docsToDettach: DocShape[],
) => {
  const { id: additionalIncomeID } = updatedValues;

  const promises = [];
  promises.push(APIClient.put(`/additional_income/${additionalIncomeID}`, updatedValues));

  docsToDettach.forEach((doc: DocShape) => {
    // So we don't remove documents that were not reviewed yet since they don't appear
    // in the multiselect dropdown
    if (!!findFieldValue(doc, "document_description")) {
      promises.push(detachDocumentFromIncomes(doc));
    }
  });
  docsToAttach.forEach((doc: DocShape) => {
    promises.push(attachIncomeToDocument(doc, additionalIncomeID));
  });

  return Promise.all(promises);
};

/**
 * Creates an AdditionalIncome record and links it to a document
 * @param  {AdditionalIncomeForm} formValues The form values from OtherIncomeModalForm
 */
export const addOtherIncome = async (formValues: AdditionalIncomeForm, documents: DocShape[]) => {
  const {
    borrower_entity,
    include_in_other,
    include_in_ratios,
    loan_application_id,
    notes,
    operator_income_type,
    associated_document_ids,
  } = formValues;

  const initialPayload: AdditionalIncomePayload = {
    borrower_entity,
    include_in_other,
    include_in_ratios,
    loan_application_id,
    notes,
    operator_income_type,
  };

  return createAdditionalIncome(initialPayload).then(
    (resp: { additional_income: AdditionalIncomeShape }) => {
      associated_document_ids.forEach((id: Number) => {
        const selectedDoc = documents.find(doc => doc.id === id);
        attachIncomeToDocument(selectedDoc, resp.additional_income.id);
      });
    },
  );
};

export const updateIncomeAggregationRules = (
  appID,
  updatedAggregations: IncomeAggregationPayload[],
) => {
  return APIClient.put(`/${appID}/income_aggregations`, updatedAggregations);
};
